import { useCallback, useEffect, useMemo } from "react";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import ShareReport from "../../../sv/components/viewer/common/sharing/ShareReport.tsx";
import type {
  ReportCompany,
  SharedItemType,
  SharedReport,
} from "../../../sv/components/viewer/common/sharing/ShareReport.types.ts";
import useUserContext from "../../../../../../context/useUserContext";
import type { ReportAuthorizationInfo } from "../../../../../../api/types/svTypes.ts";

interface ShareReportWrapperProps {
  report: SharedReport;
  itemType?: SharedItemType;
  onClose: () => void;
  updateReportAuthorizationInfo: (
    reportId: string,
    authorizationInfo: ReportAuthorizationInfo,
    inheritFromParent: boolean
  ) => void;
}
export default function ShareReportWrapper({
  report,
  itemType,
  onClose,
  updateReportAuthorizationInfo,
}: ShareReportWrapperProps) {
  const { id, name, email, clients } = useUserContext();
  const { sendNotification } = useNotificationContext();

  const reportCompany = useMemo((): ReportCompany | undefined => {
    const client = clients.find((c) => c.clientCode === report.clientCode);
    return client
      ? { clientCode: client.clientCode, name: client.title, logoMarkUrl: client.branding.logoMarkUrl }
      : undefined;
  }, [clients, report.clientCode]);

  useEffect(() => {
    if (!reportCompany) {
      sendNotification("Failed to load report company. Refresh page and try again.");
      onClose();
    }
  }, [reportCompany, sendNotification, onClose]);

  const handleAuthorizationUpdated = useCallback(
    (authorization: ReportAuthorizationInfo, inheritFromParent: boolean) => {
      updateReportAuthorizationInfo(report.reportId, authorization, inheritFromParent);
    },
    [updateReportAuthorizationInfo, report.reportId]
  );

  return (
    <ShareReport
      report={report}
      itemType={itemType}
      currentUser={{ id, name, email }}
      reportCompany={reportCompany!}
      onClose={onClose}
      onAuthorizationUpdated={handleAuthorizationUpdated}
    />
  );
}
